import { log } from "helpers/logger"
import { BLOCKED_REASONS } from "./types"
import type { BlockedReason } from "./types"

export const SCHEDULED_WORKFLOW_ERROR_CODES = {
  limitReached: "SCHEDULED_WORKFLOW_LIMIT_REACHED",
  draftInFlight: "SCHEDULED_WORKFLOW_DRAFT_IN_FLIGHT",
  draftIdConflict: "SCHEDULED_WORKFLOW_DRAFT_ID_CONFLICT",
  configVersionConflict: "SCHEDULED_WORKFLOW_CONFIG_VERSION_CONFLICT",
  platformPaused: "SCHEDULED_WORKFLOW_PLATFORM_PAUSED",
  patAuthorizationError: "SCHEDULED_WORKFLOW_PAT_AUTHORIZATION_ERROR",
  credentialInvalid: "SCHEDULED_WORKFLOW_CREDENTIAL_INVALID",
  budgetExceeded: "SCHEDULED_WORKFLOW_BUDGET_EXCEEDED",
  createUpstreamError: "SCHEDULED_WORKFLOW_CREATE_UPSTREAM_ERROR",
  budgetServiceUnavailable: "SCHEDULED_WORKFLOW_BUDGET_SERVICE_UNAVAILABLE",
  inputUnprocessableEntity: "SCHEDULED_WORKFLOW_INPUT_UNPROCESSABLE_ENTITY",
  skillsUnresolved: "SCHEDULED_WORKFLOW_SKILLS_UNRESOLVED",
  skillsDuplicate: "SCHEDULED_WORKFLOW_SKILLS_DUPLICATE",
  storeServiceUnavailable: "SCHEDULED_WORKFLOW_STORE_SERVICE_UNAVAILABLE",
  skillCatalogServiceUnavailable: "SCHEDULED_WORKFLOW_SKILL_CATALOG_SERVICE_UNAVAILABLE",
  createServiceUnavailable: "SCHEDULED_WORKFLOW_CREATE_SERVICE_UNAVAILABLE",
  createManagedAgentUnavailable: "SCHEDULED_WORKFLOW_CREATE_MANAGED_AGENT_UNAVAILABLE",
  updateUpstreamError: "SCHEDULED_WORKFLOW_UPDATE_UPSTREAM_ERROR",
  updateInFlight: "SCHEDULED_WORKFLOW_UPDATE_IN_FLIGHT",
  updateArchived: "SCHEDULED_WORKFLOW_UPDATE_ARCHIVED",
  pauseArchived: "SCHEDULED_WORKFLOW_PAUSE_ARCHIVED",
  activateArchived: "SCHEDULED_WORKFLOW_ACTIVATE_ARCHIVED",
  archiveUpstreamError: "SCHEDULED_WORKFLOW_ARCHIVE_UPSTREAM_ERROR",
  skillsWriteAccess: "SCHEDULED_WORKFLOW_SKILLS_WRITE_ACCESS",
  requestValidationUnprocessableEntity: "SCHEDULED_WORKFLOW_REQUEST_VALIDATION_UNPROCESSABLE_ENTITY",
  idNotFound: "SCHEDULED_WORKFLOW_ID_NOT_FOUND",
  resourceConflict: "SCHEDULED_WORKFLOW_RESOURCE_CONFLICT",
  deliverableNotFound: "SCHEDULED_WORKFLOW_DELIVERABLE_NOT_FOUND",
  fileNotFound: "SCHEDULED_WORKFLOW_FILE_NOT_FOUND",
  runArchived: "SCHEDULED_WORKFLOW_RUN_ARCHIVED",
  runBlocked: "SCHEDULED_WORKFLOW_RUN_BLOCKED",
  runRecordServiceUnavailable: "SCHEDULED_WORKFLOW_RUN_RECORD_SERVICE_UNAVAILABLE",
  runOutcomeServiceUnavailable: "SCHEDULED_WORKFLOW_RUN_OUTCOME_SERVICE_UNAVAILABLE",
  runUpstreamError: "SCHEDULED_WORKFLOW_RUN_UPSTREAM_ERROR",
  runManagedAgentUnavailable: "SCHEDULED_WORKFLOW_RUN_MANAGED_AGENT_UNAVAILABLE",
  featuresServiceUnavailable: "SCHEDULED_WORKFLOW_FEATURES_SERVICE_UNAVAILABLE",
  managedAgentAuthorizationError: "SCHEDULED_WORKFLOW_MANAGED_AGENT_AUTHORIZATION_ERROR",
  userAuthorizationError: "SCHEDULED_WORKFLOW_USER_AUTHORIZATION_ERROR",
  scheduleInternalError: "SCHEDULED_WORKFLOW_SCHEDULE_INTERNAL_ERROR",
  deliverableInternalError: "SCHEDULED_WORKFLOW_DELIVERABLE_INTERNAL_ERROR",
  unexpectedInternalError: "SCHEDULED_WORKFLOW_UNEXPECTED_INTERNAL_ERROR",
  proxyServiceUnavailable: "SCHEDULED_WORKFLOW_PROXY_SERVICE_UNAVAILABLE",
  proxyUpstreamError: "SCHEDULED_WORKFLOW_PROXY_UPSTREAM_ERROR",
  proxyManagedAgentDisabled: "SCHEDULED_WORKFLOW_PROXY_MANAGED_AGENT_DISABLED",
  proxyAiNotActivated: "SCHEDULED_WORKFLOW_PROXY_AI_NOT_ACTIVATED",
} as const

type Codes = typeof SCHEDULED_WORKFLOW_ERROR_CODES

export type ScheduledWorkflowErrorCode = Codes[keyof Codes]

type EmptyDetails = Record<string, never>

export const SCHEDULED_WORKFLOW_INPUT_FIELDS = [
  "name",
  "description",
  "initial_message",
  "skills",
  "email_results",
  "draft_id",
  "schedule",
  "schedule.type",
  "schedule.days",
  "schedule.time",
  "schedule.timezone",
  "schedule.every_hours",
  "schedule.start",
  "schedule.end",
  "account_id",
  "user_id",
  "limit",
  "cursor",
  "shared_workflow_ids",
  "subscribed",
] as const

export type ScheduledWorkflowInputField = typeof SCHEDULED_WORKFLOW_INPUT_FIELDS[number]

export interface ScheduledWorkflowRequestValidationError {
  type: string
  loc: (string | number)[]
  msg: string
  input?: unknown
  ctx?: Record<string, string>
}

export interface ScheduledWorkflowErrorDetails {
  SCHEDULED_WORKFLOW_LIMIT_REACHED: { limit: number, count: number }
  SCHEDULED_WORKFLOW_DRAFT_IN_FLIGHT: EmptyDetails
  SCHEDULED_WORKFLOW_DRAFT_ID_CONFLICT: EmptyDetails
  SCHEDULED_WORKFLOW_CONFIG_VERSION_CONFLICT: { currentConfigVersion: number }
  SCHEDULED_WORKFLOW_PLATFORM_PAUSED: EmptyDetails
  SCHEDULED_WORKFLOW_PAT_AUTHORIZATION_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_CREDENTIAL_INVALID: EmptyDetails
  SCHEDULED_WORKFLOW_BUDGET_EXCEEDED: { resetsAt: string }
  SCHEDULED_WORKFLOW_CREATE_UPSTREAM_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_BUDGET_SERVICE_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_INPUT_UNPROCESSABLE_ENTITY: { field: ScheduledWorkflowInputField | null }
  SCHEDULED_WORKFLOW_SKILLS_UNRESOLVED: { skills: string[] }
  SCHEDULED_WORKFLOW_SKILLS_DUPLICATE: { skills: string[] }
  SCHEDULED_WORKFLOW_STORE_SERVICE_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_SKILL_CATALOG_SERVICE_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_CREATE_SERVICE_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_CREATE_MANAGED_AGENT_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_REQUEST_VALIDATION_UNPROCESSABLE_ENTITY: { errors: ScheduledWorkflowRequestValidationError[] }
  SCHEDULED_WORKFLOW_ID_NOT_FOUND: EmptyDetails
  SCHEDULED_WORKFLOW_RESOURCE_CONFLICT: EmptyDetails
  SCHEDULED_WORKFLOW_DELIVERABLE_NOT_FOUND: EmptyDetails
  SCHEDULED_WORKFLOW_FILE_NOT_FOUND: EmptyDetails
  SCHEDULED_WORKFLOW_RUN_ARCHIVED: EmptyDetails
  SCHEDULED_WORKFLOW_RUN_BLOCKED: { blockedReason: BlockedReason }
  SCHEDULED_WORKFLOW_RUN_RECORD_SERVICE_UNAVAILABLE: { runId: string, sessionId: string | null }
  SCHEDULED_WORKFLOW_RUN_OUTCOME_SERVICE_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_RUN_UPSTREAM_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_RUN_MANAGED_AGENT_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_FEATURES_SERVICE_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_MANAGED_AGENT_AUTHORIZATION_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_USER_AUTHORIZATION_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_SCHEDULE_INTERNAL_ERROR: { scheduledWorkflowId: string }
  SCHEDULED_WORKFLOW_DELIVERABLE_INTERNAL_ERROR: { deliverableId: string, reason: string }
  SCHEDULED_WORKFLOW_UNEXPECTED_INTERNAL_ERROR: { reason: string }
  SCHEDULED_WORKFLOW_PROXY_SERVICE_UNAVAILABLE: EmptyDetails
  SCHEDULED_WORKFLOW_PROXY_UPSTREAM_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_PROXY_MANAGED_AGENT_DISABLED: EmptyDetails
  SCHEDULED_WORKFLOW_PROXY_AI_NOT_ACTIVATED: EmptyDetails
  SCHEDULED_WORKFLOW_UPDATE_UPSTREAM_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_UPDATE_IN_FLIGHT: EmptyDetails
  SCHEDULED_WORKFLOW_UPDATE_ARCHIVED: EmptyDetails
  SCHEDULED_WORKFLOW_PAUSE_ARCHIVED: EmptyDetails
  SCHEDULED_WORKFLOW_ACTIVATE_ARCHIVED: EmptyDetails
  SCHEDULED_WORKFLOW_ARCHIVE_UPSTREAM_ERROR: EmptyDetails
  SCHEDULED_WORKFLOW_SKILLS_WRITE_ACCESS: { skills: string[] }
}

export interface ScheduledWorkflowErrorOf<C extends ScheduledWorkflowErrorCode> {
  code: C
  message: string
  details: ScheduledWorkflowErrorDetails[C]
}

export type ScheduledWorkflowError = { [C in ScheduledWorkflowErrorCode]: ScheduledWorkflowErrorOf<C> }[ScheduledWorkflowErrorCode]

export type ScheduledWorkflowErrorStatus = 403 | 404 | 409 | 422 | 429 | 500 | 502 | 503

export const SCHEDULED_WORKFLOW_ERROR_STATUS: Record<ScheduledWorkflowErrorCode, ScheduledWorkflowErrorStatus> = {
  SCHEDULED_WORKFLOW_LIMIT_REACHED: 409,
  SCHEDULED_WORKFLOW_DRAFT_IN_FLIGHT: 409,
  SCHEDULED_WORKFLOW_DRAFT_ID_CONFLICT: 409,
  SCHEDULED_WORKFLOW_CONFIG_VERSION_CONFLICT: 409,
  SCHEDULED_WORKFLOW_PLATFORM_PAUSED: 409,
  SCHEDULED_WORKFLOW_PAT_AUTHORIZATION_ERROR: 403,
  SCHEDULED_WORKFLOW_CREDENTIAL_INVALID: 403,
  SCHEDULED_WORKFLOW_BUDGET_EXCEEDED: 429,
  SCHEDULED_WORKFLOW_CREATE_UPSTREAM_ERROR: 502,
  SCHEDULED_WORKFLOW_BUDGET_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_INPUT_UNPROCESSABLE_ENTITY: 422,
  SCHEDULED_WORKFLOW_SKILLS_UNRESOLVED: 422,
  SCHEDULED_WORKFLOW_SKILLS_DUPLICATE: 422,
  SCHEDULED_WORKFLOW_STORE_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_SKILL_CATALOG_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_CREATE_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_CREATE_MANAGED_AGENT_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_REQUEST_VALIDATION_UNPROCESSABLE_ENTITY: 422,
  SCHEDULED_WORKFLOW_ID_NOT_FOUND: 404,
  SCHEDULED_WORKFLOW_RESOURCE_CONFLICT: 409,
  SCHEDULED_WORKFLOW_DELIVERABLE_NOT_FOUND: 404,
  SCHEDULED_WORKFLOW_FILE_NOT_FOUND: 404,
  SCHEDULED_WORKFLOW_RUN_ARCHIVED: 409,
  SCHEDULED_WORKFLOW_RUN_BLOCKED: 409,
  SCHEDULED_WORKFLOW_RUN_RECORD_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_RUN_OUTCOME_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_RUN_UPSTREAM_ERROR: 502,
  SCHEDULED_WORKFLOW_RUN_MANAGED_AGENT_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_FEATURES_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_MANAGED_AGENT_AUTHORIZATION_ERROR: 403,
  SCHEDULED_WORKFLOW_USER_AUTHORIZATION_ERROR: 403,
  SCHEDULED_WORKFLOW_SCHEDULE_INTERNAL_ERROR: 500,
  SCHEDULED_WORKFLOW_DELIVERABLE_INTERNAL_ERROR: 500,
  SCHEDULED_WORKFLOW_UNEXPECTED_INTERNAL_ERROR: 500,
  SCHEDULED_WORKFLOW_PROXY_SERVICE_UNAVAILABLE: 503,
  SCHEDULED_WORKFLOW_PROXY_UPSTREAM_ERROR: 502,
  SCHEDULED_WORKFLOW_PROXY_MANAGED_AGENT_DISABLED: 404,
  SCHEDULED_WORKFLOW_PROXY_AI_NOT_ACTIVATED: 403,
  SCHEDULED_WORKFLOW_UPDATE_UPSTREAM_ERROR: 502,
  SCHEDULED_WORKFLOW_UPDATE_IN_FLIGHT: 503,
  SCHEDULED_WORKFLOW_UPDATE_ARCHIVED: 409,
  SCHEDULED_WORKFLOW_PAUSE_ARCHIVED: 409,
  SCHEDULED_WORKFLOW_ACTIVATE_ARCHIVED: 409,
  SCHEDULED_WORKFLOW_ARCHIVE_UPSTREAM_ERROR: 502,
  SCHEDULED_WORKFLOW_SKILLS_WRITE_ACCESS: 422,
}

export type ScheduledWorkflowRunStartOutcome = "started" | "unknown"

const RUN_START_OUTCOMES: Partial<Record<ScheduledWorkflowErrorCode, ScheduledWorkflowRunStartOutcome>> = {
  SCHEDULED_WORKFLOW_RUN_RECORD_SERVICE_UNAVAILABLE: "started",
  SCHEDULED_WORKFLOW_RUN_OUTCOME_SERVICE_UNAVAILABLE: "unknown",
  SCHEDULED_WORKFLOW_PROXY_SERVICE_UNAVAILABLE: "unknown",
  SCHEDULED_WORKFLOW_PROXY_UPSTREAM_ERROR: "unknown",
  SCHEDULED_WORKFLOW_UNEXPECTED_INTERNAL_ERROR: "unknown",
}

const REFUSED_BEFORE_FORWARDING_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 404, 409, 413, 422, 429])

function refusedBeforeForwarding(status: number): boolean {
  return REFUSED_BEFORE_FORWARDING_STATUSES.has(status)
}

const NOT_FOUND_CODES = new Set<ScheduledWorkflowErrorCode>([
  SCHEDULED_WORKFLOW_ERROR_CODES.idNotFound,
  SCHEDULED_WORKFLOW_ERROR_CODES.deliverableNotFound,
  SCHEDULED_WORKFLOW_ERROR_CODES.fileNotFound,
])

const KNOWN_CODES = new Set<string>(Object.values(SCHEDULED_WORKFLOW_ERROR_CODES))

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string")
}

function isBlockedReason(value: unknown): value is BlockedReason {
  return BLOCKED_REASONS.some((reason) => reason === value)
}

function isRequestValidationError(value: unknown): value is ScheduledWorkflowRequestValidationError {
  return isRecord(value)
    && typeof value.type === "string"
    && typeof value.msg === "string"
    && Array.isArray(value.loc)
    && value.loc.every((segment) => typeof segment === "string" || typeof segment === "number")
    && (value.ctx === undefined || (isRecord(value.ctx) && Object.values(value.ctx).every((entry) => typeof entry === "string")))
}

function isInputField(value: unknown): value is ScheduledWorkflowInputField {
  return SCHEDULED_WORKFLOW_INPUT_FIELDS.some((field) => field === value)
}

function isKnownCode(value: unknown): value is ScheduledWorkflowErrorCode {
  return typeof value === "string" && KNOWN_CODES.has(value)
}

function narrowBody(value: unknown): ScheduledWorkflowError | null {
  if (!isRecord(value) || !isKnownCode(value.code)) return null
  const code = value.code
  const message = typeof value.message === "string" ? value.message : ""
  const details = isRecord(value.details) ? value.details : {}
  switch (code) {
    case SCHEDULED_WORKFLOW_ERROR_CODES.limitReached: {
      const { limit, count } = details
      if (typeof limit !== "number" || typeof count !== "number") return null
      return { code, message, details: { limit, count } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.configVersionConflict: {
      const { currentConfigVersion } = details
      if (typeof currentConfigVersion !== "number") return null
      return { code, message, details: { currentConfigVersion } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.budgetExceeded: {
      const { resetsAt } = details
      if (typeof resetsAt !== "string") return null
      return { code, message, details: { resetsAt } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.inputUnprocessableEntity: {
      const { field } = details
      if (isInputField(field)) return { code, message, details: { field } }
      log(new Error(`${code} named an unknown field: ${String(field)}`))
      return { code, message, details: { field: null } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.runBlocked: {
      const { blockedReason } = details
      if (!isBlockedReason(blockedReason)) return null
      return { code, message, details: { blockedReason } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.runRecordServiceUnavailable: {
      const { runId, sessionId } = details
      if (typeof runId !== "string" || (typeof sessionId !== "string" && sessionId !== null)) return null
      return { code, message, details: { runId, sessionId } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.scheduleInternalError: {
      const { scheduledWorkflowId } = details
      if (typeof scheduledWorkflowId !== "string") return null
      return { code, message, details: { scheduledWorkflowId } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.deliverableInternalError: {
      const { deliverableId, reason } = details
      if (typeof deliverableId !== "string" || typeof reason !== "string") return null
      return { code, message, details: { deliverableId, reason } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.unexpectedInternalError: {
      const { reason } = details
      if (typeof reason !== "string") return null
      return { code, message, details: { reason } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.requestValidationUnprocessableEntity: {
      const { errors } = details
      if (!Array.isArray(errors)) {
        log(new Error(`${code} carried no errors list`))
        return { code, message, details: { errors: [] } }
      }
      const validationErrors = errors.filter(isRequestValidationError)
      if (validationErrors.length < errors.length) log(new Error(`${code} carried ${errors.length - validationErrors.length} malformed errors`))
      return { code, message, details: { errors: validationErrors } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsUnresolved:
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsDuplicate:
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsWriteAccess: {
      const { skills } = details
      if (!isStringArray(skills)) return null
      return { code, message, details: { skills } }
    }
    case SCHEDULED_WORKFLOW_ERROR_CODES.updateUpstreamError:
    case SCHEDULED_WORKFLOW_ERROR_CODES.updateInFlight:
    case SCHEDULED_WORKFLOW_ERROR_CODES.updateArchived:
    case SCHEDULED_WORKFLOW_ERROR_CODES.pauseArchived:
    case SCHEDULED_WORKFLOW_ERROR_CODES.activateArchived:
    case SCHEDULED_WORKFLOW_ERROR_CODES.archiveUpstreamError:
    case SCHEDULED_WORKFLOW_ERROR_CODES.resourceConflict:
      return { code, message, details: {} }
    default:
      return { code, message, details: {} }
  }
}

function parsedJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function bodyCandidates(value: unknown): unknown[] {
  if (typeof value === "string") return bodyCandidates(parsedJson(value))
  if (!isRecord(value)) return []
  return [value, ...(isRecord(value.error) || typeof value.error === "string" ? bodyCandidates(value.error) : [])]
}

export function narrowScheduledWorkflowError(value: unknown): ScheduledWorkflowError | null {
  for (const candidate of bodyCandidates(value)) {
    const narrowed = narrowBody(candidate)
    if (narrowed !== null) return narrowed
  }
  return null
}

export function scheduledWorkflowErrorOf(error: unknown): ScheduledWorkflowError | null {
  if (!isRecord(error)) return narrowScheduledWorkflowError(error)
  return narrowScheduledWorkflowError(error.error)
    ?? narrowScheduledWorkflowError(error.response)
    ?? narrowScheduledWorkflowError(error)
}

export function isScheduledWorkflowNotFound(error: unknown): boolean {
  const code = scheduledWorkflowErrorOf(error)?.code
  return code !== undefined && NOT_FOUND_CODES.has(code)
}

function httpStatusOf(error: unknown): number | null {
  if (!isRecord(error)) return null
  if (typeof error.status === "number") return error.status
  if (isRecord(error.response) && typeof error.response.status === "number") return error.response.status
  return typeof error.code === "number" ? error.code : null
}

export function runStartOutcomeOf(error: unknown): ScheduledWorkflowRunStartOutcome | null {
  if (error == null) return null
  const workflowError = scheduledWorkflowErrorOf(error)
  if (workflowError !== null) return RUN_START_OUTCOMES[workflowError.code] ?? null
  const status = httpStatusOf(error)
  return status !== null && refusedBeforeForwarding(status) ? null : "unknown"
}
